import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, Mail, Trash2, UserPlus } from "lucide-react";
import { api } from "@/lib/api";
import { StaffShell } from "@/components/layout/StaffShell";
import { adminNav } from "@/lib/nav";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CenterSpinner } from "@/components/common/States";

const statusTone = { pending: "default", used: "secondary", expired: "outline", revoked: "destructive" };

function copy(link) {
  navigator.clipboard?.writeText(link).then(
    () => toast.success("Invite link copied"),
    () => toast.error("Could not copy — select and copy manually"),
  );
}

export default function AdminInvites() {
  const qc = useQueryClient();
  const [role, setRole] = useState("vendor");
  const [restrict, setRestrict] = useState(false);
  const [email, setEmail] = useState("");

  const { data, isLoading } = useQuery({ queryKey: ["admin-invites"], queryFn: () => api("/admin/invites") });

  const create = useMutation({
    mutationFn: () =>
      api("/admin/invites", {
        method: "POST",
        json: { role, email: restrict ? email.trim() : email.trim() || null, email_locked: restrict, base_url: window.location.origin },
      }),
    onSuccess: (inv) => {
      qc.invalidateQueries({ queryKey: ["admin-invites"] });
      copy(inv.link);
      toast.success(inv.email_sent ? `Invite emailed to ${inv.email}` : "Invite created — link copied");
      setEmail("");
      setRestrict(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const revoke = useMutation({
    mutationFn: (id) => api(`/admin/invites/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-invites"] });
      toast.success("Invite revoked");
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <StaffShell items={adminNav} roleLabel="Admin" title="Invites">
      <p className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
        <UserPlus className="h-4 w-4 text-secondary" /> Send sign-up links so people can join as vendors or drivers. New mobile-app users are always customers.
      </p>

      <Card className="mb-6 rounded-2xl p-5">
        <h3 className="font-display text-lg">Create an invite</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="rounded-full" data-testid="invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="vendor">Vendor</SelectItem>
                <SelectItem value="driver">Driver</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Invitee email {restrict ? "(required)" : "(optional — for emailing the link)"}</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" className="rounded-full" data-testid="invite-email" />
          </div>
          <div className="flex items-center gap-3 sm:col-span-2">
            <Switch checked={restrict} onCheckedChange={setRestrict} id="restrict" data-testid="invite-restrict" />
            <Label htmlFor="restrict" className="cursor-pointer text-sm font-normal text-muted-foreground">
              Lock this invite to the email above (the invitee must sign in with that exact Google account)
            </Label>
          </div>
        </div>
        <Button
          className="mt-4 rounded-full font-display"
          onClick={() => (restrict && !email.trim() ? toast.error("Enter an email to restrict the invite") : create.mutate())}
          disabled={create.isPending}
          data-testid="invite-create"
        >
          {create.isPending ? "Creating…" : "Create invite link"}
        </Button>
      </Card>

      {isLoading ? (
        <CenterSpinner />
      ) : (
        <Card className="overflow-hidden rounded-2xl">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data || []).map((inv) => (
                <TableRow key={inv.invite_id} data-testid={`invite-${inv.invite_id}`}>
                  <TableCell className="font-medium capitalize">{inv.role}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {inv.email || "—"} {inv.email_locked ? <Badge variant="outline" className="ml-1 rounded-full text-[10px]">locked</Badge> : null}
                  </TableCell>
                  <TableCell><Badge variant={statusTone[inv.computed_status] || "secondary"} className="rounded-full font-normal capitalize">{inv.computed_status}</Badge></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(inv.expires_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => copy(inv.link)} data-testid={`invite-copy-${inv.invite_id}`} title="Copy link">
                        <Copy className="h-4 w-4" />
                      </Button>
                      {inv.email ? (
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => (window.location.href = `mailto:${inv.email}?subject=Your Oppad invite&body=${encodeURIComponent(inv.link)}`)} title="Email link">
                          <Mail className="h-4 w-4" />
                        </Button>
                      ) : null}
                      {inv.computed_status === "pending" ? (
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full text-destructive" onClick={() => revoke.mutate(inv.invite_id)} data-testid={`invite-revoke-${inv.invite_id}`} title="Revoke">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!data?.length && <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">No invites yet. Create one above.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      )}
    </StaffShell>
  );
}
